import { useEffect, useState } from 'react'
import { FilePlus2, Trash2, X } from 'lucide-react'
import { useEditor } from '../store/editor'
import { deleteProject, listProjects, loadProject, loginUrl, type ProjectSummary } from '../lib/api'
import { formatShort, normalizeProject } from '../lib/model'
import { setUrlProject, writeLocal } from '../lib/persist'
import { startNewProject } from '../lib/actions'

function when(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export default function ProjectsDialog() {
  const user = useEditor((s) => s.user)
  const currentId = useEditor((s) => s.serverId)
  const [items, setItems] = useState<ProjectSummary[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const close = () => useEditor.getState().set({ dialog: null })

  useEffect(() => {
    if (!user) return
    listProjects().then(setItems).catch((e) => setErr((e as Error).message))
  }, [user])

  const open = async (id: string) => {
    const st = useEditor.getState()
    if (st.dirty && st.project.clips.length && id !== st.serverId &&
        !window.confirm('Hay cambios sin guardar en el proyecto actual. ¿Abrir otro igualmente?')) return
    st.set({ busy: 'Abriendo proyecto…', dialog: null })
    try {
      const r = await loadProject(id)
      const p = normalizeProject(r.data)
      p.name = r.name || p.name
      useEditor.getState().setProject(p, { serverId: r.id })
      setUrlProject(r.id)
      writeLocal()
    } catch (e) {
      useEditor.getState().toast(`No se pudo abrir: ${(e as Error).message}`, 'error')
    } finally {
      useEditor.getState().set({ busy: null })
    }
  }

  const remove = async (e: React.MouseEvent, p: ProjectSummary) => {
    e.stopPropagation()
    if (!window.confirm(`¿Eliminar «${p.name}»? No se puede deshacer.`)) return
    try {
      await deleteProject(p.id)
      setItems((xs) => xs?.filter((x) => x.id !== p.id) ?? null)
      if (p.id === currentId) useEditor.getState().set({ serverId: null })
    } catch (er) {
      useEditor.getState().toast(`No se pudo eliminar: ${(er as Error).message}`, 'error')
    }
  }

  return (
    <div className="overlay" onPointerDown={(e) => { if (e.target === e.currentTarget) close() }}>
      <div className="dialog wide">
        <h3>
          Mis proyectos
          <button className="btn ghost icon sm" onClick={close}><X size={16} /></button>
        </h3>
        {!user ? (
          <>
            <p>Inicia sesión para ver los proyectos guardados en tu cuenta.</p>
            <div className="actions"><a className="btn primary" href={loginUrl()}>Iniciar sesión</a></div>
          </>
        ) : (
          <>
            {err && <p style={{ color: 'var(--danger)' }}>{err}</p>}
            {!items && !err && <div className="spinner" />}
            <div className="proj-grid">
              <button className="proj" style={{ display: 'grid', placeItems: 'center', minHeight: 150, color: 'inherit' }}
                onClick={() => { close(); startNewProject() }}>
                <div style={{ textAlign: 'center' }}><FilePlus2 size={26} /><div style={{ marginTop: 6 }}>Proyecto nuevo</div></div>
              </button>
              {items?.map((p) => (
                <div key={p.id} className="proj" onClick={() => open(p.id)} style={p.id === currentId ? { borderColor: 'var(--accent)' } : undefined}>
                  <div className="th" style={{ backgroundImage: p.thumbnailUrl ? `url(${p.thumbnailUrl})` : undefined }} />
                  <div className="inf">
                    <b>{p.name}</b>
                    <span className="hint">{when(p.updatedAt)}{p.durationSec ? ` · ${formatShort(p.durationSec)}` : ''}</span>
                  </div>
                  <button className="btn icon sm danger del" title="Eliminar" onClick={(e) => remove(e, p)}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            {items && !items.length && <p className="hint" style={{ marginTop: 12 }}>Todavía no has guardado proyectos. Se guardan solos mientras editas.</p>}
          </>
        )}
        {!user && <p className="hint"><a href={loginUrl()}>¿No tienes cuenta? Crea una gratis</a></p>}
      </div>
    </div>
  )
}
